import {
  ChartDataset,
  ChartStyle
} from "state/ui/editor/elements/ChartElement";
import {
  bandScale,
  datasetDomain,
  defaultPlotArea,
  formatTickLabel,
  generateTicks,
  linearScale,
  seriesColorAt,
  xAxisLabelOffsetFor
} from "../chartScales";

interface Props {
  dataset: ChartDataset;
  style: ChartStyle;
  width: number;
  height: number;
}

const BarChart = ({ dataset, style, width, height }: Props) => {
  const plot = defaultPlotArea(width, height, {
    showLegend: style.showLegend,
    showAxes: style.showAxes,
    fontSize: style.fontSize
  });

  const domain = datasetDomain(dataset);
  const x = linearScale(domain, [plot.left, plot.left + plot.width]);
  const y = bandScale(dataset.headers, [plot.top, plot.top + plot.height]);
  const seriesCount = Math.max(dataset.rows.length, 1);
  const innerBandwidth = y.bandwidth / seriesCount;
  const ticks = generateTicks(domain[0], domain[1]);
  const baseline = x.scale(0);

  return (
    <g>
      {style.showGridlines &&
        ticks.map(tick => (
          <line
            key={`grid-${tick}`}
            x1={x.scale(tick)}
            x2={x.scale(tick)}
            y1={plot.top}
            y2={plot.top + plot.height}
            stroke="#E5E7EB"
            strokeWidth={1}
          />
        ))}

      {style.showAxes &&
        ticks.map(tick => (
          <text
            key={`x-${tick}`}
            x={x.scale(tick)}
            y={plot.top + plot.height + xAxisLabelOffsetFor(style.fontSize)}
            fontSize={style.fontSize}
            fill={style.color}
            fontFamily={style.fontFamily}
            textAnchor="middle"
          >
            {formatTickLabel(tick)}
          </text>
        ))}

      {dataset.rows.map((row, rowIndex) => {
        const color = seriesColorAt(style.seriesColors, rowIndex);
        return (
          <g key={`series-${rowIndex}`}>
            {row.values.map((value, valueIndex) => {
              if (valueIndex >= dataset.headers.length) return null;
              const bandTop = y.scaleAt(valueIndex) + rowIndex * innerBandwidth;
              const barLeft = Math.min(x.scale(value), baseline);
              const barWidth = Math.abs(x.scale(value) - baseline);
              // Negative bars extend left of the baseline, so place the label at
              // the bar's left end (anchored end) rather than right of zero.
              const isNegative = value < 0;
              return (
                <g key={`bar-${rowIndex}-${valueIndex}`}>
                  <rect
                    x={barLeft}
                    y={bandTop}
                    width={Math.max(barWidth, 0)}
                    height={Math.max(innerBandwidth - 2, 1)}
                    fill={color}
                  />
                  {style.showDataLabels && (
                    <text
                      x={isNegative ? barLeft - 4 : barLeft + barWidth + 4}
                      y={bandTop + innerBandwidth / 2}
                      fontSize={style.fontSize}
                      fill={style.color}
                      fontFamily={style.fontFamily}
                      textAnchor={isNegative ? "end" : "start"}
                      dominantBaseline="middle"
                    >
                      {formatTickLabel(value)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}

      {style.showAxes &&
        dataset.headers.map((header, colIndex) => (
          <text
            key={`y-${colIndex}`}
            x={plot.left - 6}
            y={y.scaleAt(colIndex) + y.bandwidth / 2}
            fontSize={style.fontSize}
            fill={style.color}
            fontFamily={style.fontFamily}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {header}
          </text>
        ))}
    </g>
  );
};

export default BarChart;
